<script lang="ts" setup>
import { stringify } from 'qs'
import { useUserStore } from '~/store/user'
import { LazyImage } from '@/components/lazy-image'
import { useUtilDetectAllStore } from '~/store/common/index'
import {
  type GoodsImageConfig,
  GOODS_IMAGE_TYPE,
  GOODS_IMAGE_RESIZE_TYPE,
  GOODS_IMAGE_FORMAT,
  GOODS_IMAGE_QUALITY
} from '~/components/common/modules/types/goods-image-type'
import { NO_IMAGE_URL } from '~/constants/common/good-image'
import { useMounted } from '@vueuse/core'

const isMounted = useMounted()

/**
 * 상품 이미지 속성
 * @typedef {Object} props
 * @property {Nubmer | string} goodsCd - [필수] 상품코드
 * @property {Object} config - 이미지 설정
 * @property {string} config.type - 이미지 타입
 */
const props = defineProps({
  goodsCd: Number as PropType<number>,
  config: Object as PropType<GoodsImageConfig>
})

const emit = defineEmits<{
  (event: 'onError'): void
  (event: 'onLoad', result: boolean): void
}>()

// 기본값 셋팅
const type = computed(() => props.config?.type ?? GOODS_IMAGE_TYPE.REP)
const resize = computed(() => props.config?.resize ?? GOODS_IMAGE_RESIZE_TYPE.CONTAIN)
const format = computed(() => props.config?.format ?? GOODS_IMAGE_FORMAT.WEBP)
const quality = computed(() => props.config?.quality ?? GOODS_IMAGE_QUALITY.BASE)
const alt = computed(() => props.config?.alt ?? '')

// 에러 중복발생 방지
const isError = ref<boolean>(false)
// 와이드 에러
const isWideError = ref<boolean>(false)
const isImgLoaded = ref<boolean>(false)

// 서브타입일 경우 인덱스 설정
const index = computed(() => {
  if (props.config?.index && Number(props.config?.index) >= 0) {
    return Number(props.config?.index)
  } else {
    return undefined
  }
})

// 대표타입 유무
const isRepType = computed(() => type.value === GOODS_IMAGE_TYPE.REP || isWideError.value)
// 서브타입 유무
const isSubType = computed(() => type.value === GOODS_IMAGE_TYPE.SUB)
// 와이드타입 유무
const isWideType = computed(() => type.value === GOODS_IMAGE_TYPE.WIDE)

/**
 * [이미지 타입 값]
 * 1. 대표 이미지 || 와이드 이미지에서 에러 발생한 경우
 * 2. 서브 이미지
 * 3. 와이드 이미지
 * 4. 이외의 경우
 */
const typeValue = computed(() => {
  if (isRepType.value) {
    return 0
  } else if (isSubType.value) {
    return index.value ?? 1
  } else if (isWideType.value) {
    return 'X6'
  } else {
    return 0
  }
})

/**
 * 파일이름
 * 대표 이미지: 42851464_0.jpg
 * 서브 이미지: 42851464_1.jpg
 * 와이드 이미지: 42851464_X6.jpg
 */
const fileName = computed(() => {
  if (index.value && index.value >= 0) {
    return `${props.goodsCd}_${index.value}.jpg`
  } else {
    return `${props.goodsCd}_${typeValue.value}.jpg`
  }
})

/**
 * 파일경로
 * https://product-image.dev-nsmall.com/new/42988862_X6.jpg
 */
const filePath = computed(() => {
  return `${$config.PRODUCT_URL}/new/${fileName.value}`
})

const utilDetectAllStore = useUtilDetectAllStore()
const { detectInfo } = storeToRefs(utilDetectAllStore)
const ratio = detectInfo.value.ratio

// 디바이스 픽셀 밀도를 고려한 width
const resizeWidth = computed(() => {
  if (props.config?.width) {
    return props.config.width * ratio
  } else {
    return undefined
  }
})

// 디바이스 픽셀 밀도를 고려한 height
const resizeHeight = computed(() => {
  if (props.config?.height) {
    return props.config.height * ratio
  } else {
    return undefined
  }
})

// NO IMAGE SQUARE
const noImageSquare = computed(() => {
  if (props.config?.isAdult) {
    return NO_IMAGE_URL.ADULT.SQUARE
  } else {
    return NO_IMAGE_URL.BASE.SQUARE
  }
})

// NO IMAGE WIDE
const noImageWide = computed(() => {
  if (props.config?.isAdult) {
    return NO_IMAGE_URL.ADULT.WIDE
  } else {
    return NO_IMAGE_URL.BASE.WIDE
  }
})

// NO IMAGE
const noImage = computed(() => {
  const type = props.config?.type
  if (type === GOODS_IMAGE_TYPE.WIDE) {
    return noImageWide.value
  } else {
    return noImageSquare.value
  }
})

/**
 * 쿼리 파라메터
 * resizeWidth, resizeHeight: 디바이 해상도 대응 사이즈
 * resize: crop | fill | contain: 리사이즈 유형
 * format: webp | jpg
 * quality: 80
 */
const queryParameter = computed(() => (isSrcSet: boolean = true) => {
  const queryInfo: any = {}
  if (resizeWidth.value && resizeHeight.value) queryInfo.s = `${resizeWidth.value}x${resizeHeight.value}`
  if (resize.value) queryInfo.t = `${resize.value}`
  if (format.value && isSrcSet === true) queryInfo.f = `${format.value}`
  if (quality.value) queryInfo.q = `${quality.value}`
  return stringify(queryInfo)
})

const userStore = useUserStore()
const { custInfo } = storeToRefs(userStore)

// 로그인 유무
const isLogin = computed(() => !!custInfo.value?.loginId)

// 성인인증유무
const isCertifyAdult = computed(() => {
  return isLogin.value && custInfo.value?.adltCertYn === 'Y'
})

const imageSrc = computed(() => {
  // 성인상품 && 성인인증유무 === false
  if (props.config?.isAdult && isCertifyAdult.value === false) {
    return noImage.value
  }

  if (isError.value) return noImage.value

  return getFilePath(true)
})

const imageSrcSet = computed(() => {
  // 성인상품 && 성인인증유무 === false
  if (props.config?.isAdult && isCertifyAdult.value === false) {
    return noImage.value
  }

  if (isError.value) return noImage.value
  return getFilePath(true)
})

// 이미지 타입에 따른 경로 분기 처리
function getFilePath(isSrcSet: boolean = false) {
  if (isRepType.value) {
    return `${filePath.value}?${queryParameter.value(isSrcSet)}`
  } else if (isSubType.value) {
    return `${filePath.value}?${queryParameter.value(isSrcSet)}`
  } else if (isWideType.value) {
    return `${filePath.value}`
  }
}

function onLoadImg() {
  isImgLoaded.value = true
  emit('onLoad', isImgLoaded.value)
}

function onError(e: Event) {
  if (isError.value) return
  emit('onError')
  if (type.value === GOODS_IMAGE_TYPE.WIDE && isWideError.value === false) {
    isWideError.value = true
    return
  }
  isError.value = true
  isImgLoaded.value = true
  emit('onLoad', isImgLoaded.value)
}
</script>

<template>
  <template v-if="isMounted">
    <LazyImage :src="imageSrc" :srcset="imageSrcSet" :alt="(alt as string)" @error="onError" @on-load="onLoadImg" />
  </template>
</template>
